import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  notification
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

interface Role {
  RoleId: number;
  RoleKey: string;
  RoleName: string;
}

interface RoleFormData {
  roleKey: string;
  roleName: string;
}

const { confirm } = Modal;

const RoleManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [form] = Form.useForm();

  // Load roles khi component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/roles', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      } else {
        throw new Error('Failed to fetch roles');
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      message.error('Không thể tải danh sách vai trò');
    } finally {
      setLoading(false);
    }
  };

  const showAddModal = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (role: Role) => {
    setEditingRole(role);
    form.setFieldsValue({
      roleKey: role.RoleKey,
      roleName: role.RoleName
    });
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingRole(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const url = editingRole ? `/api/v1/roles/${editingRole.RoleId}` : '/api/v1/roles';
      const method = editingRole ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        },
        body: JSON.stringify({
          roleKey: values.roleKey,
          roleName: values.roleName
        })
      });

      if (response.ok) {
        message.success(editingRole ? 'Cập nhật vai trò thành công' : 'Thêm vai trò mới thành công');
        setModalVisible(false);
        form.resetFields();
        setEditingRole(null);
        fetchRoles();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Lỗi khi lưu vai trò');
      }
    } catch (error: any) {
      console.error('Error saving role:', error);
      message.error(error.message || 'Không thể lưu vai trò');
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = (role: Role) => {
    confirm({
      title: `Bạn có chắc chắn muốn xóa vai trò "${role.RoleName}"?`,
      icon: <ExclamationCircleOutlined />,
      content: 'Lưu ý: Nếu có người dùng đang sử dụng vai trò này, việc xóa sẽ không thành công.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk() {
        handleDelete(role.RoleId);
      }
    });
  };

  const handleDelete = async (roleId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        message.success('Xóa vai trò thành công');
        fetchRoles();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Lỗi khi xóa vai trò');
      }
    } catch (error: any) {
      console.error('Error deleting role:', error);
      if (error.message) {
        notification.error({
          message: 'Không thể xóa vai trò',
          description: error.message,
          duration: 5
        });
      } else {
        message.error('Không thể xóa vai trò');
      }
    } finally {
      setLoading(false);
    }
  };

  const isProtectedRole = (roleKey: string): boolean => {
    return ['admin', 'teacher', 'student', 'phongkhaothi'].includes(roleKey);
  };

  const getRoleTagColor = (roleKey: string): string => {
    switch (roleKey) {
      case 'admin': return 'red';
      case 'teacher': return 'green';
      case 'student': return 'blue';
      case 'phongkhaothi': return 'purple';
      default: return 'default';
    }
  };

  const columns: ColumnsType<Role> = [
    {
      title: 'Mã vai trò',
      dataIndex: 'RoleKey',
      key: 'RoleKey',
      sorter: (a, b) => a.RoleKey.localeCompare(b.RoleKey),
      render: (text: string) => <code>{text}</code>
    },
    {
      title: 'Tên vai trò',
      dataIndex: 'RoleName',
      key: 'RoleName',
      sorter: (a, b) => a.RoleName.localeCompare(b.RoleName),
      render: (text: string, record: Role) => (
        <Space>
          <span>{text}</span>
          <Tag color={getRoleTagColor(record.RoleKey)}>
            {isProtectedRole(record.RoleKey) ? 'Hệ thống' : 'Tùy chỉnh'}
          </Tag>
        </Space>
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 200,
      render: (_, record: Role) => (
        <Space>
          <Tooltip title="Sửa vai trò">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Xóa vai trò"
            description={`Bạn có chắc chắn muốn xóa vai trò "${record.RoleName}"?`}
            onConfirm={() => handleDelete(record.RoleId)}
            okText="Xóa"
            cancelText="Hủy"
            disabled={isProtectedRole(record.RoleKey)}
          >
            <Tooltip title={isProtectedRole(record.RoleKey) ? 'Không thể xóa vai trò hệ thống' : 'Xóa vai trò'}>
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                disabled={isProtectedRole(record.RoleKey)}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            Quản lý vai trò hệ thống
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              Thêm vai trò mới
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchRoles}
            >
              Làm mới
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="RoleId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} vai trò`
          }}
          scroll={{ x: 600 }}
        />
      </Card>

      {/* Modal thêm/sửa vai trò */}
      <Modal
        title={editingRole ? 'Cập nhật vai trò' : 'Thêm vai trò mới'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={loading}
        okText={editingRole ? 'Cập nhật' : 'Thêm'}
        cancelText="Hủy"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="roleKey"
            label="Mã vai trò (không dấu, không khoảng trắng)"
            rules={[
              { required: true, message: 'Vui lòng nhập mã vai trò' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Mã vai trò chỉ chứa chữ cái, số và dấu gạch dưới' }
            ]}
          >
            <Input 
              placeholder="Ví dụ: admin, teacher, student" 
              disabled={editingRole ? isProtectedRole(editingRole.RoleKey) : false}
            />
          </Form.Item>
          <Form.Item
            name="roleName"
            label="Tên vai trò"
            rules={[{ required: true, message: 'Vui lòng nhập tên vai trò' }]}
          >
            <Input placeholder="Ví dụ: Quản trị viên, Giáo viên, Sinh viên" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagementPage;
